import React, { useEffect, useState } from "react";
import { Alert, Button, Container, Form, Spinner, Table } from "react-bootstrap";
import axios from "../utils/api";
import toast from "react-hot-toast";

const API_URL = process.env.REACT_APP_API_URL_BASE || "http://localhost:6020/";

function InvoiceItems() {
  const [vendorQuery, setVendorQuery] = useState("");
  const [invoiceQuery, setInvoiceQuery] = useState("");
  const [invoices, setInvoices] = useState([]);
  const [expandedInvoices, setExpandedInvoices] = useState(() => new Set());
  const [expandedRawLines, setExpandedRawLines] = useState(() => new Set());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchInvoices = async (params = {}) => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(`${API_URL}api/invoice`, { params });
      if (!response.data?.success) {
        throw new Error(response.data?.message || "Failed to fetch invoices");
      }
      const items = response.data.items || [];
      setInvoices(items);
      setExpandedInvoices(new Set());
    } catch (err) {
      console.error("Fetch invoices error:", err);
      const msg = err.message || "Error loading invoices";
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInvoices();
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    fetchInvoices({
      vendor_name: vendorQuery || undefined,
      invoice_number: invoiceQuery || undefined,
      page: 1,
      limit: 50,
    });
  };

  const handleClear = () => {
    setVendorQuery("");
    setInvoiceQuery("");
    fetchInvoices({ page: 1, limit: 50 });
  };

  const toggleExpanded = (id) => {
    setExpandedInvoices((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const toggleRawLines = (key) => {
    setExpandedRawLines((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  const RawLinesToggle = ({ itemKey, item, colSpan }) => {
    const lines = item.raw_lines;
    if (!lines || !lines.length) return null;
    const isOpen = expandedRawLines.has(itemKey);
    return isOpen ? (
      <tr key={`${itemKey}-raw`}>
        <td colSpan={colSpan} style={{ padding: "4px 12px", background: "#f8f9fa" }}>
          <pre style={{ margin: 0, fontSize: "0.75em", whiteSpace: "pre-wrap", color: "#555" }}>{lines.join("\n")}</pre>
        </td>
      </tr>
    ) : null;
  };

  const handleDelete = async (inv) => {
    const label = inv.invoice_number
      ? `Invoice #${inv.invoice_number}`
      : "this invoice";
    if (!window.confirm(`Delete ${label}? This cannot be undone.`)) return;

    try {
      const res = await axios.delete(`${API_URL}api/invoice/${inv._id}`);
      if (res.data?.success) {
        toast.success(`Deleted ${label}`);
        setInvoices((prev) => prev.filter((i) => i._id !== inv._id));
      } else {
        toast.error(res.data?.message || "Delete failed");
      }
    } catch (err) {
      console.error("Delete invoice error:", err);
      toast.error(err.response?.data?.message || "Failed to delete invoice");
    }
  };

  return (
  <Container className="invoice-upload-container mt-4">
      <h2 className="mb-4">Invoices</h2>

      <div className="card p-4 mb-4">
        <h5 className="mb-3">Search Saved Invoices</h5>
        <Form onSubmit={handleSearch}>
          <div className="row g-3">
            <div className="col-md-5">
              <Form.Label>Vendor</Form.Label>
              <Form.Control
                value={vendorQuery}
                onChange={(e) => setVendorQuery(e.target.value)}
                placeholder="Southern Glazer's..."
              />
            </div>
            <div className="col-md-5">
              <Form.Label>Invoice Number</Form.Label>
              <Form.Control
                value={invoiceQuery}
                onChange={(e) => setInvoiceQuery(e.target.value)}
                placeholder="04311551"
              />
            </div>
            <div className="col-md-2 d-flex align-items-end gap-2">
              <Button type="submit" variant="primary" disabled={loading}>
                {loading ? <Spinner size="sm" animation="border" /> : "Search"}
              </Button>
              <Button type="button" variant="outline-secondary" onClick={handleClear}>
                Clear
              </Button>
            </div>
          </div>
        </Form>
      </div>

      {error && (
        <Alert variant="danger" dismissible onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <div className="card p-4 mb-4">
        <h5 className="mb-3">Saved Invoices</h5>
        {loading ? (
          <div className="text-center py-4">
            <Spinner animation="border" />
          </div>
        ) : invoices.length === 0 ? (
          <p className="text-muted mb-0">No invoices found.</p>
        ) : (
          <div className="table-responsive sticky-table sticky-table--outer">
            <Table striped bordered hover>
              <thead className="table-light">
                <tr>
                  <th>Invoice #</th>
                  <th>Vendor</th>
                  <th>Invoice Date</th>
                  <th>Total Items</th>
                  <th>Total Amount</th>
                  <th>Saved</th>
                  <th>Items</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {invoices.map((inv) => {
                  const isExpanded = expandedInvoices.has(inv._id);
                  const lineItems = inv.items || inv.lineItems || [];
                  return (
                    <React.Fragment key={inv._id}>
                      <tr>
                        <td>
                          <a
                            href={`/invoice-detail/${inv._id}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ textDecoration: "none" }}
                          >
                            {inv.invoice_number || "—"}
                          </a>
                        </td>
                        <td>{inv.vendor_name || "—"}</td>
                        <td>{inv.invoice_date || "—"}</td>
                        <td>{inv.totalItems ?? lineItems.length ?? "—"}</td>
                        <td>
                          {typeof inv.totalAmount === "number"
                            ? `$${inv.totalAmount.toFixed(2)}`
                            : "—"}
                        </td>
                        <td>{inv.createdAt ? new Date(inv.createdAt).toLocaleString() : "—"}</td>
                        <td>
                          <Button
                            size="sm"
                            variant={isExpanded ? "outline-primary" : "primary"}
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleExpanded(inv._id);
                            }}
                          >
                            {isExpanded ? "Hide" : "Show"}
                          </Button>
                        </td>
                        <td>
                          <Button
                            size="sm"
                            variant="outline-danger"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDelete(inv);
                            }}
                          >
                            🗑️
                          </Button>
                          {inv.pdfPath && (
                            <a
                              href={`${API_URL}api/invoice/${inv._id}/pdf`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="btn btn-sm btn-outline-secondary ms-1"
                              title="View original PDF"
                              onClick={(e) => e.stopPropagation()}
                            >
                              📄
                            </a>
                          )}
                        </td>
                      </tr>
                      {isExpanded && (
                        <tr>
                          <td colSpan={8}>
                            {lineItems.length ? (() => {
                              // Detect Pitco invoices: items have out_of_stock field or vendor is Pitco
                              const isPitco = (inv.vendor_name || "").toLowerCase().includes("pitco") ||
                                lineItems.some((it) => "out_of_stock" in it && "product_number" in it && "crv_per_unit" in it);
                              // Detect Coke invoices: items have sku field but no product_number
                              const isCoke = !isPitco && lineItems.some((it) => it.sku && !it.product_number);
                              return isPitco ? (
                              <div className="table-responsive sticky-table sticky-table--inner">
                                <Table striped bordered hover size="sm" className="mb-0">
                                  <thead className="table-light">
                                    <tr>
                                      <th>#</th>
                                      <th>UPC</th>
                                      <th>Item#</th>
                                      <th>Description</th>
                                      <th>Qty</th>
                                      <th>U/M</th>
                                      <th>PPC</th>
                                      <th>Cost/Unit</th>
                                      <th>POS Price</th>
                                      <th>Margin</th>
                                      <th>Retail</th>
                                      <th>GP%</th>
                                      <th>Allow</th>
                                      <th>CRV</th>
                                      <th>Total</th>
                                      <th>OOS</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {lineItems.map((item, idx) => {
                                      const costPerUnit = typeof item.price_per_unit === "number"
                                        ? item.price_per_unit : null;
                                      const posPrice = typeof item.posPrice === "number"
                                        ? item.posPrice
                                        : typeof item.posCents === "number"
                                        ? item.posCents / 100
                                        : null;
                                      const margin = costPerUnit !== null && posPrice !== null && posPrice > 0
                                        ? ((posPrice - costPerUnit) / posPrice) * 100
                                        : null;
                                      const isOOS = item.out_of_stock;
                                      const rawKey = `${inv._id}-pitco-${idx}`;
                                      return (
                                      <React.Fragment key={rawKey}>
                                      <tr style={isOOS ? { opacity: 0.5 } : {}}>
                                        <td>
                                          {item.raw_lines?.length ? (
                                            <span role="button" style={{cursor:"pointer",userSelect:"none"}} onClick={() => toggleRawLines(rawKey)}>
                                              {expandedRawLines.has(rawKey) ? "−" : "+"}
                                            </span>
                                          ) : null}{" "}{idx + 1}
                                        </td>
                                        <td><code style={{fontSize:"0.8em"}}>{item.pack_upc || "—"}</code></td>
                                        <td>{item.product_number || "—"}</td>
                                        <td>{item.description || "—"}</td>
                                        <td>{item.qty ?? "—"}</td>
                                        <td>{item.uom || "—"}</td>
                                        <td>{item.ppc ?? "—"}</td>
                                        <td>{costPerUnit !== null ? `$${costPerUnit.toFixed(2)}` : "—"}</td>
                                        <td>{posPrice !== null ? `$${posPrice.toFixed(2)}` : "—"}</td>
                                        <td style={margin !== null ? { color: margin >= 25 ? "green" : margin >= 10 ? "orange" : "red", fontWeight: 600 } : {}}>
                                          {margin !== null ? `${margin.toFixed(1)}%` : "—"}
                                        </td>
                                        <td>{typeof item.retail === "number" ? `$${item.retail.toFixed(2)}` : "—"}</td>
                                        <td>{typeof item.gp_pct === "number" ? `${item.gp_pct.toFixed(1)}%` : "—"}</td>
                                        <td>{typeof item.allow === "number" && item.allow !== 0 ? `$${item.allow.toFixed(2)}` : "—"}</td>
                                        <td>{typeof item.crv_total === "number" && item.crv_total > 0 ? `$${item.crv_total.toFixed(2)}` : "—"}</td>
                                        <td>{typeof item.total === "number" ? `$${item.total.toFixed(2)}` : "—"}</td>
                                        <td>{isOOS ? <span title="Out of Stock" style={{color:"red",fontWeight:700}}>⊘</span> : ""}</td>
                                      </tr>
                                      <RawLinesToggle itemKey={rawKey} item={item} colSpan={16} />
                                      </React.Fragment>
                                      );
                                    })}
                                  </tbody>
                                </Table>
                              </div>
                              ) : isCoke ? (
                              <div className="table-responsive sticky-table sticky-table--inner">
                                <Table striped bordered hover size="sm" className="mb-0">
                                  <thead className="table-light">
                                    <tr>
                                      <th>#</th>
                                      <th>SKU</th>
                                      <th>Description</th>
                                      <th>UPC</th>
                                      <th>Qty</th>
                                      <th>PPC</th>
                                      <th>Cost/Unit</th>
                                      <th>POS Price</th>
                                      <th>Margin</th>
                                      <th>Discount</th>
                                      <th>CRV</th>
                                      <th>Total</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {lineItems.map((item, idx) => {
                                      const costPerUnit = typeof item.price_per_unit === "number"
                                        ? item.price_per_unit
                                        : (() => {
                                            const total = typeof item.total === "number" ? item.total : null;
                                            const ppc = parseFloat(item.ppc);
                                            const qty = parseFloat(item.qty);
                                            const d = ppc * qty;
                                            return total !== null && d > 0 ? +(total / d).toFixed(2) : null;
                                          })();
                                      const posPrice = typeof item.posPrice === "number"
                                        ? item.posPrice
                                        : typeof item.posCents === "number"
                                        ? item.posCents / 100
                                        : null;
                                      const margin = costPerUnit !== null && posPrice !== null && posPrice > 0
                                        ? ((posPrice - costPerUnit) / posPrice) * 100
                                        : null;
                                      const cokeKey = `${inv._id}-coke-${idx}`;
                                      return (
                                      <React.Fragment key={cokeKey}>
                                      <tr>
                                        <td>
                                          {item.raw_lines?.length ? (
                                            <span role="button" style={{cursor:"pointer",userSelect:"none"}} onClick={() => toggleRawLines(cokeKey)}>
                                              {expandedRawLines.has(cokeKey) ? "−" : "+"}
                                            </span>
                                          ) : null}{" "}{idx + 1}
                                        </td>
                                        <td><code>{item.sku || "—"}</code></td>
                                        <td>{item.description || "—"}</td>
                                        <td>{item.pack_upc || <span className="text-muted" style={{fontSize:"0.8em"}}>no map</span>}</td>
                                        <td>{item.qty ?? "—"}</td>
                                        <td>{item.ppc ?? "—"}</td>
                                        <td>{costPerUnit !== null ? `$${costPerUnit.toFixed(2)}` : "—"}</td>
                                        <td>{posPrice !== null ? `$${posPrice.toFixed(2)}` : "—"}</td>
                                        <td style={margin !== null ? { color: margin >= 25 ? "green" : margin >= 10 ? "orange" : "red", fontWeight: 600 } : {}}>
                                          {margin !== null ? `${margin.toFixed(1)}%` : "—"}
                                        </td>
                                        <td>{typeof item.discount === "number" ? `$${item.discount.toFixed(2)}` : "—"}</td>
                                        <td>{typeof item.crv === "number" ? `$${item.crv.toFixed(2)}` : "—"}</td>
                                        <td>{typeof item.total === "number" ? `$${item.total.toFixed(2)}` : "—"}</td>
                                      </tr>
                                      <RawLinesToggle itemKey={cokeKey} item={item} colSpan={12} />
                                      </React.Fragment>
                                      );
                                    })}
                                  </tbody>
                                </Table>
                              </div>
                              ) : (
                              <div className="table-responsive sticky-table sticky-table--inner">
                                <Table striped bordered hover size="sm" className="mb-0">
                                  <thead className="table-light">
                                    <tr>
                                      <th>#</th>
                                      <th>Description</th>
                                      <th>Case UPC</th>
                                      <th>Pack UPC</th>
                                      <th>Price/Unit</th>
                                      <th>POS Price</th>
                                      <th>%</th>
                                      <th>Qty</th>
                                      <th>PPC</th>
                                      <th>Disc</th>
                                      <th>Adj</th>
                                      <th>Price</th>
                                      <th>Total</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {lineItems.map((item, idx) => {
                                      const ppu = (() => {
                                        const total = typeof item.total === "number" ? item.total : null;
                                        const ppc = parseFloat(item.ppc);
                                        const qty = parseFloat(item.qty);
                                        const divisor = ppc * qty;
                                        if (total !== null && divisor > 0) return total / divisor;
                                        return null;
                                      })();
                                      const posPrice = typeof item.posPrice === "number"
                                        ? item.posPrice
                                        : typeof item.posCents === "number"
                                        ? item.posCents / 100
                                        : null;
                                      const margin = ppu !== null && posPrice !== null && posPrice > 0
                                        ? ((posPrice - ppu) / posPrice) * 100
                                        : null;
                                      const defKey = `${inv._id}-item-${idx}`;
                                      return (
                                      <React.Fragment key={defKey}>
                                      <tr>
                                        <td>
                                          {item.raw_lines?.length ? (
                                            <span role="button" style={{cursor:"pointer",userSelect:"none"}} onClick={() => toggleRawLines(defKey)}>
                                              {expandedRawLines.has(defKey) ? "−" : "+"}
                                            </span>
                                          ) : null}{" "}{idx + 1}
                                        </td>
                                        <td>{item.description || "—"}</td>
                                        <td>{item.case_upc || "—"}</td>
                                        <td>{item.pack_upc || "—"}</td>
                                        <td>
                                          {ppu !== null ? `$${ppu.toFixed(2)}` : "—"}
                                        </td>
                                        <td>
                                          {posPrice !== null ? `$${posPrice.toFixed(2)}` : "—"}
                                        </td>
                                        <td>
                                          {margin !== null ? `${margin.toFixed(1)}%` : "—"}
                                        </td>
                                        <td>{item.qty ?? "—"}</td>
                                        <td>{item.ppc ?? "—"}</td>
                                        <td>{item.disc ?? "—"}</td>
                                        <td>{item.adj ?? "—"}</td>
                                        <td>
                                          {typeof item.price === "number"
                                            ? `$${item.price.toFixed(2)}`
                                            : "—"}
                                        </td>
                                        <td>
                                          {typeof item.total === "number"
                                            ? `$${item.total.toFixed(2)}`
                                            : "—"}
                                        </td>
                                      </tr>
                                      <RawLinesToggle itemKey={defKey} item={item} colSpan={13} />
                                      </React.Fragment>
                                      );
                                    })}
                                  </tbody>
                                </Table>
                              </div>
                              );
                            })() : (
                              <span className="text-muted">No items for this invoice.</span>
                            )}
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </Table>
          </div>
        )}
      </div>
    </Container>
  );
}

export default InvoiceItems;
